import React, { useState, useRef, useMemo } from 'react';
import { useI18n } from '../../context/I18nContext';
import { COLORI_MAPPINGS } from '../../constants/colorConstants';
import { formatColorForDisplay } from '../../utils/colorUtils';
import useDropdown from '../../hooks/useDropdown';
import CableColorRect from '../ui/CableColorRect';

const ColorSelector = ({ value, onChange, language, className = "" }) => {
  const { t } = useI18n();
  const [searchTerm, setSearchTerm] = useState('');
  const colors = COLORI_MAPPINGS[language] || COLORI_MAPPINGS.it;
  const searchInputRef = useRef(null);

  const { isOpen, toggleDropdown, closeDropdown, dropdownRef } = useDropdown(false, {
    autoFocus: searchInputRef,
    onClose: () => setSearchTerm('')
  });

  const filteredColors = useMemo(() => {
    if (!searchTerm.trim()) return colors;

    return colors.filter(color =>
      color.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatColorForDisplay(color).toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [colors, searchTerm]);

  const handleColorSelect = (colorName) => {
    onChange(colorName);
    closeDropdown();
  };

  // Renderizza preview del colore
  const renderColorPreview = (colorName, isSelected = false) => {
    return (
      <div className="flex items-center gap-3">
        <CableColorRect
          color={colorName}
          width="20px"
          height="20px"
          borderRadius="4px"
          className="transition-all duration-200 flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="text-base">
            {formatColorForDisplay(colorName)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Pulsante principale */}
      <button
        type="button"
        onClick={toggleDropdown}
        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-left focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between min-h-[44px]"
      >
        <div className="flex items-center gap-3">
          <CableColorRect
            color={value}
            width="20px"
            height="20px"
            borderRadius="4px"
            className="transition-all duration-200 flex-shrink-0"
          />

          <div className="flex-1 min-w-0">
            <div className="text-base">
              {formatColorForDisplay(value)}
            </div>
          </div>
        </div>

        <svg
          className={`w-4 h-4 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          {/* Campo di ricerca */}
          <div className="p-2 border-b">
            <input
              ref={searchInputRef}
              type="text"
              placeholder={t('selectors.searchColor')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm border rounded focus:ring-1 focus:ring-blue-500"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && filteredColors.length > 0) {
                  handleColorSelect(filteredColors[0]);
                }
                e.stopPropagation();
              }}
            />
          </div>

          {/* Lista opzioni */}
          <div className="max-h-48 overflow-auto">
            {filteredColors.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">
                {t('selectors.noColorFound')}
              </div>
            ) : (
              filteredColors.map((colorName) => (
                <button
                  key={colorName}
                  type="button"
                  onClick={() => handleColorSelect(colorName)}
                  className={`w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none transition-colors ${value === colorName ? 'bg-blue-100' : ''
                    }`}
                  role="option"
                  aria-selected={value === colorName}
                >
                  {renderColorPreview(colorName, value === colorName)}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorSelector;
